import React, { useState, useEffect } from 'react';
import { Cookie, ExternalLink } from 'lucide-react';

const setCookieConsent = (value: string) => {
    try {
        localStorage.setItem('cookieConsent', value);
        console.log('Cookie consent saved:', value);
    } catch (error) {
        console.warn('localStorage not available:', error);
    }
};

const getCookieConsent = (): string | null => {
    try {
        return localStorage.getItem('cookieConsent');
    } catch (error) {
        console.warn('localStorage not available:', error);
        return null;
    }
};

interface CookieBannerProps {
    assistantName: string;
    onConsentChange?: (consented: boolean) => void;
    forceVisible?: boolean;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({
                                                              onConsentChange,
                                                              forceVisible = false
                                                          }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (forceVisible) {
            setIsVisible(true);
            return;
        }

        const consentStatus = getCookieConsent();

        if (consentStatus === 'accepted') {
            setIsVisible(false);
            onConsentChange?.(true);
            console.log('Cookie consent already accepted - banner hidden');
        } else if (consentStatus === 'declined') {
            setIsVisible(false);
            onConsentChange?.(false);
            console.log('Cookie consent already declined - banner hidden');
        } else {
            setIsVisible(true);
            console.log('No cookie consent found - showing banner');
        }
    }, [onConsentChange, forceVisible]);

    const handleAccept = () => {
        console.log('User accepted cookies');
        setIsVisible(false);
        setCookieConsent('accepted');
        onConsentChange?.(true);

        // Dispatch event for GTM service
        console.log('Dispatching cookieConsentAccepted event');
        window.dispatchEvent(new Event('cookieConsentAccepted'));
    };

    const handleDecline = () => {
        console.log('User declined cookies');
        setIsVisible(false);
        setCookieConsent('declined');
        onConsentChange?.(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-[#1a1a1a] border-t border-gray-600 shadow-lg z-50 animate-in slide-in-from-bottom duration-300">
            <div className="py-3 sm:py-4 px-3 sm:px-6">
                <div className="container mx-auto">
                    <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                        <Cookie className="text-cyan-400" size={20} />
                        <span className="text-xs sm:text-sm font-medium text-gray-300">Welcome to GPTforme</span>
                    </div>

                    <div className="text-center mb-3 sm:mb-4">
                        <div className="text-xs sm:text-sm text-gray-300 leading-relaxed px-2">
                            By continuing, you agree to our{' '}
                            <a
                                href="/GPTforme_terms_of_use.pdf"
                                className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Use
                                <ExternalLink size={10} className="sm:w-3 sm:h-3" />
                            </a>
                            {' '}and{' '}
                            <a
                                href="/GPTforme_privacy_policy.pdf"
                                className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy Policy
                                <ExternalLink size={10} className="sm:w-3 sm:h-3" />
                            </a>
                            . We use cookies to improve your experience and deliver relevant ads. See our{' '}
                            <a
                                href="/GPTforme_cookie_policy.pdf"
                                className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Cookie Policy
                                <ExternalLink size={10} className="sm:w-3 sm:h-3" />
                            </a>
                            {' '}for details.
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={handleDecline}
                            className="bg-gray-700 hover:bg-gray-600 hover:shadow-lg text-gray-300 font-semibold rounded-full transition-all duration-300 ease-in-out px-4 sm:px-6 py-2 transform hover:scale-105 text-sm"
                        >
                            Decline
                        </button>
                        <button
                            onClick={handleAccept}
                            className="bg-cyan-400 hover:bg-cyan-500 hover:shadow-lg text-black font-semibold rounded-full transition-all duration-300 ease-in-out px-4 sm:px-6 py-2 transform hover:scale-105 text-sm"
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};